import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface BalloonMathGameProps {
  onBack: () => void;
}

type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

interface Balloon {
  id: string;
  x: number;
  y: number;
  expression: string;
  answer: number;
  color: string;
  speed: number;
  size: number;
  isPopping: boolean;
}

const BALLOON_COLORS = [
  'from-rose-400 to-rose-600',
  'from-sky-400 to-sky-600',
  'from-amber-400 to-amber-600',
  'from-emerald-400 to-emerald-600',
  'from-violet-400 to-violet-600',
  'from-pink-400 to-pink-600',
  'from-cyan-400 to-cyan-600',
  'from-orange-400 to-orange-600',
];

export default function BalloonMathGame({ onBack }: BalloonMathGameProps) {
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('intermediate');
  const [duration, setDuration] = useState(3);
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [balloons, setBalloons] = useState<Balloon[]>([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [missed, setMissed] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [weeklyStats, setWeeklyStats] = useState({ total_sessions: 0, total_minutes: 0 });
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [comboMultiplier, setComboMultiplier] = useState(1);
  const [lastPopPosition, setLastPopPosition] = useState<{ x: number; y: number } | null>(null);
  const [showCombo, setShowCombo] = useState(false);

  const gameAreaRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const balloonIdCounter = useRef(0);
  const isActiveRef = useRef(false);

  // Keep isActiveRef in sync
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    loadStreak();
    loadWeeklyStats();
    // Initialize audio context on first user interaction
    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      document.removeEventListener('click', initAudio);
    };
    document.addEventListener('click', initAudio);
    return () => document.removeEventListener('click', initAudio);
  }, []);

  // Game timer
  useEffect(() => {
    let interval: number | undefined;
    if (isActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleComplete();
            return 0;
          }
          return prev - 1;
        });
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, timeRemaining]);

  // Helper function to generate expression - defined early
  const getExpression = (): { expression: string; answer: number } => {
    let expression: string;
    let answer: number;

    switch (difficulty) {
      case 'beginner': {
        const num1 = Math.floor(Math.random() * 15) + 3;
        const num2 = Math.floor(Math.random() * 10) + 2;
        const ops = ['+', '-'];
        const op = ops[Math.floor(Math.random() * ops.length)];
        expression = `${num1} ${op} ${num2}`;
        answer = op === '+' ? num1 + num2 : num1 - num2;
        break;
      }
      case 'intermediate': {
        const num1 = Math.floor(Math.random() * 12) + 2;
        const num2 = Math.floor(Math.random() * 10) + 2;
        expression = `${num1} × ${num2}`;
        answer = num1 * num2;
        break;
      }
      case 'advanced': {
        const num1 = Math.floor(Math.random() * 15) + 5;
        const num2 = Math.floor(Math.random() * 8) + 2;
        const num3 = Math.floor(Math.random() * 20) + 10;
        expression = `${num1} × ${num2} + ${num3}`;
        answer = num1 * num2 + num3;
        break;
      }
      default:
        expression = '5 + 3';
        answer = 8;
    }
    return { expression, answer };
  };

  // Balloon spawner - only spawn when no active balloon
  useEffect(() => {
    if (!isActive) return;
    
    // Small delay to ensure ref is ready
    const timeoutId = setTimeout(() => {
      if (!gameAreaRef.current) return;
      if (balloons.length === 0) {
        const gameWidth = gameAreaRef.current.offsetWidth;
        const { expression, answer } = getExpression();
        // Constant speed based on difficulty only
        const baseSpeed = difficulty === 'beginner' ? 0.15 : difficulty === 'intermediate' ? 0.2 : 0.25;
        const size = difficulty === 'advanced' ? 90 : 80;
        
        const newBalloon: Balloon = {
          id: `balloon-${Date.now()}`,
          x: Math.random() * (gameWidth - size - 20) + 10,
          y: -10,
          expression,
          answer,
          color: BALLOON_COLORS[Math.floor(Math.random() * BALLOON_COLORS.length)],
          speed: baseSpeed,
          size,
          isPopping: false
        };
        setBalloons([newBalloon]);
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [isActive, balloons.length, difficulty]);

  // Balloon animation - constant speed, no increase
  useEffect(() => {
    if (!isActive) return;

    let animationId: number;

    const animate = () => {
      setBalloons(prev => {
        if (prev.length === 0) return prev;
        
        const updated = prev.map(balloon => {
          if (balloon.isPopping) return balloon;
          // Constant speed - no multiplier
          return {
            ...balloon,
            y: balloon.y + balloon.speed
          };
        });

        // Check for balloons that reached the bottom
        const stillActive: Balloon[] = [];
        let missedCount = 0;

        updated.forEach(balloon => {
          if (balloon.y > 100 && !balloon.isPopping) {
            missedCount++;
          } else {
            stillActive.push(balloon);
          }
        });

        if (missedCount > 0) {
          setMissed(m => m + missedCount);
          setStreak(0);
          setComboMultiplier(1);
        }

        return stillActive.filter(b => !b.isPopping || b.y <= 100);
      });

      if (isActiveRef.current) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isActive]);

  const loadStreak = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('get_activity_streak', {
        p_user_id: user.id,
        p_activity_type: 'mental_maths'
      });

      if (!error && data !== null) {
        setBestStreak(data);
      }
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadWeeklyStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('get_activity_weekly_stats', {
        p_user_id: user.id,
        p_activity_type: 'mental_maths'
      });

      if (!error && data && data.length > 0) {
        setWeeklyStats(data[0]);
      }
    } catch (error) {
      console.error('Error loading weekly stats:', error);
    }
  };

  const playPopSound = useCallback(() => {
    if (!soundEnabled || !audioContextRef.current) return;

    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(800, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.15);

    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.15);

    // Add a second pop sound for effect
    const oscillator2 = ctx.createOscillator();
    const gainNode2 = ctx.createGain();

    oscillator2.connect(gainNode2);
    gainNode2.connect(ctx.destination);

    oscillator2.type = 'triangle';
    oscillator2.frequency.setValueAtTime(1200, ctx.currentTime + 0.02);
    oscillator2.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.12);

    gainNode2.gain.setValueAtTime(0.2, ctx.currentTime + 0.02);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);

    oscillator2.start(ctx.currentTime + 0.02);
    oscillator2.stop(ctx.currentTime + 0.12);
  }, [soundEnabled]);

  const handleStart = () => {
    setIsActive(true);
    setTimeRemaining(duration * 60);
    setTimeElapsed(0);
    setScore(0);
    setCorrect(0);
    setMissed(0);
    setStreak(0);
    setComboMultiplier(1);
    setBalloons([]);
    setUserAnswer('');
    balloonIdCounter.current = 0;
  };

  const handleNumberClick = (num: string) => {
    setUserAnswer(prev => prev + num);
  };

  const handleBackspace = () => {
    setUserAnswer(prev => prev.slice(0, -1));
  };

  const handleSubmitAnswer = () => {
    if (!userAnswer) return;

    const userNum = parseFloat(userAnswer);
    const matchingBalloon = balloons.find(b => !b.isPopping && Math.abs(b.answer - userNum) < 0.01);

    if (matchingBalloon) {
      playPopSound();
      setLastPopPosition({ x: matchingBalloon.x, y: matchingBalloon.y });
      setShowCombo(true);
      setTimeout(() => setShowCombo(false), 500);

      setBalloons(prev =>
        prev.map(b =>
          b.id === matchingBalloon.id ? { ...b, isPopping: true } : b
        )
      );

      // Remove popped balloon after animation
      setTimeout(() => {
        setBalloons(prev => prev.filter(b => b.id !== matchingBalloon.id));
      }, 300);

      const newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak > bestStreak) {
        setBestStreak(newStreak);
      }

      // Update combo multiplier
      const newMultiplier = Math.min(1 + Math.floor(newStreak / 3) * 0.5, 3);
      setComboMultiplier(newMultiplier);

      // Base points per pop based on difficulty
      const basePoints = difficulty === 'beginner' ? 5 : difficulty === 'intermediate' ? 10 : 20;
      
      // Add score with multiplier and increment correct count
      setCorrect(prev => prev + 1);
      setScore(prev => prev + Math.round(basePoints * newMultiplier));
    }

    setUserAnswer('');
  };

  const handleComplete = async () => {
    setIsActive(false);
    setShowResultsModal(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const totalAttempts = correct + missed;
      const accuracyVal = totalAttempts > 0 ? (correct / totalAttempts) * 100 : 0;

      await supabase.from('mind_body_activities').insert({
        user_id: user.id,
        activity_type: 'mental_maths',
        duration_minutes: duration,
        difficulty_level: difficulty,
        score: score,
        accuracy: accuracyVal,
        completed_at: new Date().toISOString()
      });

      await loadStreak();
      await loadWeeklyStats();
    } catch (error) {
      console.error('Error saving balloon math session:', error);
    }
  };

  const handleCloseModal = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      onBack();
    }, 300);
  };

  const handlePlayAgain = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      handleStart();
    }, 300);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const accuracy = (correct + missed) > 0 ? Math.round((correct / (correct + missed)) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-300 via-sky-400 to-sky-500 pb-8 overflow-hidden">
      {/* Animated clouds background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-10 left-[10%] w-32 h-16 bg-white/40 rounded-full blur-sm animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute top-20 right-[15%] w-40 h-20 bg-white/30 rounded-full blur-sm animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />
        <div className="absolute top-32 left-[50%] w-28 h-14 bg-white/35 rounded-full blur-sm animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
      </div>

      <div className="max-w-lg mx-auto relative">
        {/* Header */}
        <header className="bg-white/90 backdrop-blur-sm px-4 py-3 flex items-center justify-between sticky top-0 z-20 border-b border-sky-200 shadow-sm">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-sky-100 hover:bg-sky-200 flex items-center justify-center transition-colors"
          >
            <ArrowLeft size={18} className="text-sky-700" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xl">🎈</span>
            <h1 className="text-base font-bold text-gray-900">Balloon Pop Math</h1>
          </div>
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="w-9 h-9 rounded-xl bg-sky-100 hover:bg-sky-200 flex items-center justify-center transition-colors"
          >
            {soundEnabled ? (
              <Volume2 size={18} className="text-sky-700" />
            ) : (
              <VolumeX size={18} className="text-sky-700" />
            )}
          </button>
        </header>

        <div className="p-4 space-y-4">
          {!isActive ? (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">🔥</span>
                    <p className="text-xs font-medium text-gray-500">Best Streak</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">{bestStreak} <span className="text-sm font-medium text-gray-400">pops</span></p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">📊</span>
                    <p className="text-xs font-medium text-gray-500">This Week</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">{weeklyStats.total_sessions} <span className="text-sm font-medium text-gray-400">games</span></p>
                </div>
              </div>

              {/* Main Card */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 border border-white/50 shadow-lg">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 rounded-full flex items-center justify-center mb-3 shadow-xl shadow-pink-500/30 relative">
                    <span className="text-4xl">🎈</span>
                    <div className="absolute -bottom-1 w-1 h-6 bg-gradient-to-b from-gray-400 to-gray-600 rounded-full" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Pop the Balloons!</h2>
                  <p className="text-sm text-gray-500 mt-1">Solve equations before they float away</p>
                </div>

                {/* Duration Selection */}
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Duration</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 3, 5, 10].map((min) => (
                      <button
                        key={min}
                        onClick={() => setDuration(min)}
                        className={`py-2.5 rounded-xl font-bold text-sm transition-all active:scale-[0.95] ${
                          duration === min
                            ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg shadow-pink-500/20'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {min}m
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty Selection */}
                <div className="mb-5">
                  <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Difficulty</p>
                  <div className="space-y-2">
                    {([
                      { level: 'beginner' as DifficultyLevel, emoji: '🌱', label: 'Beginner', desc: 'Simple +/- problems' },
                      { level: 'intermediate' as DifficultyLevel, emoji: '⭐', label: 'Intermediate', desc: 'Mixed operations' },
                      { level: 'advanced' as DifficultyLevel, emoji: '🚀', label: 'Advanced', desc: 'Algebra & complex math' }
                    ]).map((item) => (
                      <button
                        key={item.level}
                        onClick={() => setDifficulty(item.level)}
                        className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all active:scale-[0.99] border-2 ${
                          difficulty === item.level
                            ? 'border-pink-500 bg-pink-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <span className="text-xl">{item.emoji}</span>
                        <div className="text-left flex-1">
                          <p className={`font-semibold text-sm ${difficulty === item.level ? 'text-pink-700' : 'text-gray-900'}`}>
                            {item.label}
                          </p>
                          <p className="text-xs text-gray-500">{item.desc}</p>
                        </div>
                        {difficulty === item.level && (
                          <div className="w-5 h-5 bg-pink-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* How to Play */}
                <div className="bg-sky-50 rounded-xl p-4 mb-5 border border-sky-100">
                  <p className="text-xs font-bold text-sky-700 mb-2">💡 How to Play</p>
                  <ul className="text-xs text-sky-600 space-y-1">
                    <li>• One balloon falls at a time with a math problem</li>
                    <li>• Type the answer and tap Submit to pop!</li>
                    <li>• Build combos for bonus points</li>
                  </ul>
                </div>

                {/* Start Button */}
                <button
                  onClick={handleStart}
                  className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 text-white py-4 rounded-xl font-bold text-sm transition-all shadow-lg shadow-pink-500/30 active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <span className="text-lg">🎈</span>
                  Start Popping!
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Active Game Stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 border border-white/50 shadow-lg">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-sm">⏱️</span>
                    <p className="text-[10px] font-medium text-gray-500">Time</p>
                  </div>
                  <p className="text-lg font-black text-gray-900">{formatTime(timeRemaining)}</p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 border border-white/50 shadow-lg">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-sm">⭐</span>
                    <p className="text-[10px] font-medium text-gray-500">Score</p>
                  </div>
                  <p className="text-lg font-black text-gray-900">{score}</p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 border border-white/50 shadow-lg relative overflow-hidden">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-sm">🔥</span>
                    <p className="text-[10px] font-medium text-gray-500">Streak</p>
                  </div>
                  <p className="text-lg font-black text-gray-900">{streak}</p>
                  {comboMultiplier > 1 && (
                    <div className="absolute top-1 right-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                      x{comboMultiplier}
                    </div>
                  )}
                </div>
              </div>

              {/* Game Area */}
              <div
                ref={gameAreaRef}
                className="relative bg-gradient-to-b from-sky-200/50 to-sky-300/50 backdrop-blur-sm rounded-2xl border border-white/50 shadow-inner overflow-hidden"
                style={{ height: '420px' }}
              >
                {/* Balloons */}
                {balloons.map(balloon => (
                  <div
                    key={balloon.id}
                    className={`absolute transition-all ${balloon.isPopping ? 'scale-150 opacity-0' : ''}`}
                    style={{
                      left: balloon.x,
                      top: `${balloon.y}%`,
                      width: balloon.size,
                      transition: balloon.isPopping ? 'all 0.3s ease-out' : 'none'
                    }}
                  >
                    {/* Balloon */}
                    <div
                      className={`relative bg-gradient-to-br ${balloon.color} rounded-full shadow-lg flex items-center justify-center p-2`}
                      style={{
                        width: balloon.size,
                        height: balloon.size * 1.2,
                        borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%'
                      }}
                    >
                      {/* Shine effect */}
                      <div className="absolute top-2 left-3 w-4 h-4 bg-white/40 rounded-full blur-sm" />
                      
                      {/* Expression */}
                      <div className="bg-white/90 rounded-lg px-2 py-1 shadow-sm">
                        <p className="text-[11px] font-bold text-gray-800 text-center whitespace-nowrap">
                          {balloon.expression}
                        </p>
                      </div>
                    </div>
                    
                    {/* String */}
                    <div className="absolute left-1/2 -translate-x-1/2 w-0.5 h-8 bg-gradient-to-b from-gray-400 to-gray-300" />
                  </div>
                ))}

                {/* Combo popup */}
                {showCombo && comboMultiplier > 1 && lastPopPosition && (
                  <div
                    className="absolute text-2xl font-black text-orange-500 animate-bounce pointer-events-none"
                    style={{
                      left: lastPopPosition.x,
                      top: `${lastPopPosition.y}%`,
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    x{comboMultiplier}!
                  </div>
                )}

                {/* Empty state */}
                {balloons.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-sky-600/50 font-medium">Balloons incoming...</p>
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-lg">
                {/* Answer Display */}
                <div className="bg-white rounded-xl p-3 mb-3 min-h-[48px] flex items-center justify-center border-2 border-gray-200">
                  <p className="text-2xl font-black text-gray-900">
                    {userAnswer || '—'}
                  </p>
                </div>

                {/* Number Pad - Compact */}
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num, idx) => (
                    <button
                      key={num}
                      onClick={() => handleNumberClick(num.toString())}
                      className={`bg-gray-100 hover:bg-gray-200 text-gray-900 text-lg font-bold py-3 rounded-xl border border-gray-200 transition-all active:scale-95 ${
                        idx === 9 ? 'col-start-2' : ''
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    onClick={() => handleNumberClick('-')}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-900 text-lg font-bold py-3 rounded-xl border border-gray-200 transition-all active:scale-95"
                  >
                    −
                  </button>
                  <button
                    onClick={() => handleNumberClick('.')}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-900 text-lg font-bold py-3 rounded-xl border border-gray-200 transition-all active:scale-95"
                  >
                    .
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-5 gap-2">
                  <button
                    onClick={handleBackspace}
                    className="col-span-2 bg-red-50 hover:bg-red-100 text-red-600 py-3 rounded-xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-1 border border-red-100"
                  >
                    ⌫ Clear
                  </button>
                  <button
                    onClick={handleSubmitAnswer}
                    disabled={!userAnswer}
                    className="col-span-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white py-3 rounded-xl font-bold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-pink-500/20"
                  >
                    Pop! 🎈
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Results Modal */}
      {showResultsModal && (
        <div
          className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
            isClosingModal ? 'bg-black/0' : 'bg-black/50'
          }`}
          onClick={handleCloseModal}
        >
          <div
            className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
              isClosingModal ? 'translate-y-full' : 'translate-y-0'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-5 pb-6">
              {/* Celebration */}
              <div className="text-center mb-4">
                <div className="text-6xl mb-2">🎉</div>
                <h2 className="text-xl font-bold text-gray-900">Time's Up!</h2>
                <p className="text-sm text-gray-500">Great balloon popping!</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-gradient-to-br from-rose-50 to-pink-50 border border-pink-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">⭐</span>
                  </div>
                  <p className="text-xl font-black text-pink-600">{score}</p>
                  <p className="text-[10px] text-pink-600 font-medium">Score</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">🎈</span>
                  </div>
                  <p className="text-xl font-black text-green-600">{correct}</p>
                  <p className="text-[10px] text-green-600 font-medium">Popped</p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">🔥</span>
                  </div>
                  <p className="text-xl font-black text-orange-600">{bestStreak}</p>
                  <p className="text-[10px] text-orange-600 font-medium">Best Streak</p>
                </div>
              </div>

              {/* Accuracy Bar */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500">Accuracy</span>
                  <span className="text-sm font-bold text-gray-900">{accuracy}%</span>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-rose-500 to-pink-600 transition-all duration-500"
                    style={{ width: `${accuracy}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                  <span>{correct} correct</span>
                  <span>{missed} missed</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleCloseModal}
                  className="py-3.5 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98]"
                >
                  Done
                </button>
                <button
                  onClick={handlePlayAgain}
                  className="py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg shadow-pink-500/20 transition-all active:scale-[0.98]"
                >
                  Play Again 🎈
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}